import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import {
  DialogService,
  DialogCloseOptions,
  DialogCloseReason,
} from '@sui/dialog';

import { ObjectEditorService } from './object-editor.service';
import { ObjectActionsService } from './object-actions.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ConfirmDialogComponent } from '../components/popups/confirm-dialog/confirm-dialog.component';
import { MoveCopyWidgetDialogComponent } from '../../shared/components/move-copy-widget-dialog/move-copy-widget-dialog.component';
import { PickerDataobjectsDialogComponent } from '../components/popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

import {
  PopupType,
  WidgetType,
  NomObjectClass,
  NomObjectTypes,
} from '../constants';
import { DataObject, Widget } from '../models/core.models';
import { WidgetOperation } from '../models/widget-operations';

import * as widgetActions from '../actions/widget.actions';
import { clearSelectedObjects } from '../actions/selected.actons';

import {
  selectCurrentObject,
  selectSelectedObjects,
  selectObjectWidgetViewProperties,
  selectSelectedPageId,
} from '../reducers';
import { AppState } from 'src/app/reducers';
import { selectWidgetViewConfig } from '../reducers/index';
import { WidgetViewConfig } from '../reducers/widget.reducer';

import { Observable, combineLatest, filter, map, take, tap } from 'rxjs';
import { PresentationService } from './presentation.service';
import { WidgetViewPresetService } from './widget-view-preset.service';
import { CurrentViewWidgetsService } from './current-view-widgets.service';
import { WidgetPersistenceService } from './widget-persistence.service';
import { ObjectPersistenceService } from './object-persistence.service';

/** A row in a projected widget-toolbar dropdown (actions / view / languages). */
export interface WidgetMenuItem {
  text?: string;
  key?: string | number;
  icon?: IconProp;
  value?: unknown;
  isSelected?: boolean;
  isToggle?: boolean;
  isRadio?: boolean;
  isSeparator?: boolean;
  radioGroup?: string;
  action?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class WidgetActionsService {
  viewConfig: WidgetViewConfig[] = [];
  private currentObject: DataObject | null = null;

  /**
   * The current view's widgets, minus placeholders, read from the list query's
   * cache entry at call time. Read on demand rather than cached in a field: a root
   * service must not hold the list, and this leaves no stale-snapshot window.
   */
  private get widgets(): Widget[] {
    return this.currentViewWidgets
      .snapshot()
      .filter(
        (widget) =>
          widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
      );
  }

  private get widgetIds(): string[] {
    return this.widgets
      .filter((widget) => widget.id)
      .map((widget) => widget.id);
  }

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);
  private presentationService = inject(PresentationService);

  private readonly widgetPersistence = inject(WidgetPersistenceService);
  private readonly objectPersistence = inject(ObjectPersistenceService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);

  /** Reactive data for the widget toolbar (consumed by app-widget-toolbar). */
  readonly selectedCount$: Observable<number>;
  readonly viewItems$: Observable<WidgetMenuItem[]>;
  readonly languageItems$: Observable<WidgetMenuItem[]>;

  private readonly widgetViewPreset = inject(WidgetViewPresetService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private objectActionService: ObjectActionsService,
    private editService: ObjectEditorService,
  ) {
    this.selectedCount$ = this.store
      .select(selectSelectedObjects)
      .pipe(map((objects) => objects.size));

    // View dropdown items — attached pivots/filters/datasources toggles.
    this.viewItems$ = combineLatest([
      this.store.select(selectObjectWidgetViewProperties),
      this.store.select(selectWidgetViewConfig),
    ]).pipe(
      map(([viewProperties, widgetViewConfig]) =>
        widgetViewConfig.map((configItem) => {
          const pathToPropertyMap = {
            pivots: 'widget_display_attached_pivots',
            filters: 'widget_display_attached_filters',
            datasources: 'widget_display_attached_data_sources',
          };
          const propertyName = pathToPropertyMap[configItem.path];
          const isSelected = viewProperties[propertyName];
          return {
            text: this.translateService.instant(configItem.label),
            key: configItem.path,
            isSelected,
            icon: configItem.icon,
            isToggle: true,
            action: () => {
              this.objectPersistence.updateViewProperties({
                ...viewProperties,
                [propertyName]: !isSelected,
              });
            },
          } as WidgetMenuItem;
        }),
      ),
    );

    // Language dropdown items
    this.languageItems$ = toObservable(
      this.widgetViewPreset.languageConfig,
    ).pipe(
      map((config) =>
        config.map(
          (configItem) =>
            ({
              text: configItem.label,
              key: configItem.path,
              value: configItem.path,
              isSelected: configItem.selected,
              isToggle: !configItem.isRadio,
              isRadio: configItem.isRadio,
              radioGroup: configItem.radioGroup,
              icon: configItem.icon,
              action: () =>
                this.widgetViewPreset.selectLanguage(configItem.label),
            }) as WidgetMenuItem,
        ),
      ),
    );

    // Keep currentObject in sync (used by the domain methods below).
    this.store
      .select(selectCurrentObject)
      .pipe(
        tap((currentObject) => (this.currentObject = currentObject)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Keep the raw view config (used by domain methods).
    this.store
      .select(selectWidgetViewConfig)
      .pipe(
        tap((cfg) => (this.viewConfig = cfg)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // * static dropdown items for both the "Actions" and "Selected" dropdowns.
  // Each item's `action` calls a domain method (dialog flows) kept below.
  getWidgetActionItems(isSelectedMode: boolean = false): WidgetMenuItem[] {
    const items: WidgetMenuItem[] = [];

    items.push(
      {
        text: this.translateService.instant(
          'widget-actions.button.copy-/-move-widgets',
        ),
        action: () => this.showCopyMoveDialog(isSelectedMode),
        icon: ['fas', 'copy'],
      },
      {
        isSeparator: true,
      },

      {
        text: this.translateService.instant(
          'widget-actions.button.mass-update-widget-settings',
        ),
        action: () => this.showMassUpdateWidgets(isSelectedMode),
        icon: ['fas', 'sliders'],
      },
      {
        isSeparator: true,
      },
      {
        text: this.translateService.instant(
          'widget-actions.button.attach-filters',
        ),
        action: isSelectedMode
          ? this.attachFiltersToSelected.bind(this)
          : this.attachFilters.bind(this),
        icon: ['fas', 'filter'],
      },
      {
        text: this.translateService.instant(
          'widget-actions.button.attach-pivots',
        ),
        action: isSelectedMode
          ? this.attachPivotsToSelected.bind(this)
          : this.attachPivots.bind(this),
        icon: ['fas', 'layer-group'],
      },
      {
        isSeparator: true,
      },
      {
        text: this.translateService.instant(
          'widget-actions.button.detach-filters',
        ),
        action: isSelectedMode
          ? this.detachFiltersFromSelected.bind(this)
          : this.detachFilters.bind(this),
        icon: ['fas', 'filter-circle-xmark'],
      },
      {
        text: this.translateService.instant(
          'widget-actions.button.detach-pivots',
        ),
        action: isSelectedMode
          ? this.detachPivotsFromSelected.bind(this)
          : this.detachPivots.bind(this),
        icon: ['fas', 'file-circle-minus'],
      },
      {
        isSeparator: true,
      },
      {
        text: this.translateService.instant(
          'widget-actions.button.remove-widgets',
        ),
        action: isSelectedMode
          ? this.deleteSelectedWidgets.bind(this)
          : this.deleteWidgets.bind(this),
        icon: ['fas', 'times'],
      },
    );

    if (isSelectedMode) {
      items.push(
        { isSeparator: true },
        {
          text: this.translateService.instant(
            'widget-actions.button.clear-selection',
          ),
          icon: ['fas', 'times'],
          action: () => {
            this.clearSelected();
          },
        },
      );
    }

    return items;
  }

  reset() {
    this.store.dispatch(widgetActions.searchWidgets({ query: '' }));
  }

  attachFilters() {
    let widgetIds = this.widgetIds;

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          popupType: PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
      )
      .subscribe((event: any) => {
        this.widgetPersistence.applySettings(widgetIds, {
          dimension_id_filter: event.data,
        });
      });
  }

  attachPivots() {
    let widgetIds = this.widgetIds;

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          popupType: PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
      )
      .subscribe((event: any) => {
        this.widgetPersistence.applySettings(widgetIds, {
          dimension_id_pivot: event.data,
        });
      });
  }

  detachFilters() {
    let widgetIds = this.widgetIds;

    const message = this.translateService.instant(
      'widget-actions.message.detach-filter',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.applySettings(widgetIds, {
            dimension_id_filter: [],
          });
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }

  detachPivots() {
    let widgetIds = this.widgetIds;

    const message = this.translateService.instant(
      'widget-actions.message.detach-pivot',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.applySettings(widgetIds, {
            dimension_id_pivot: [],
          });
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }

  deleteWidgets() {
    const message = this.translateService.instant(
      'widget-actions.message.delete-widget',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          // ! every widget in the view, placeholder included
          this.removeWidgets(
            this.currentViewWidgets.snapshot().map((widget) => widget.id),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }

  showMassUpdateWidgets(isSelectedMode: boolean = false): void {
    let widgets = this.getWidgetListBySelectedMode(isSelectedMode);
    this.editService.openMassUpdateWidgetDialog(widgets.map((w) => w.id));
  }

  getWidgetListBySelectedMode(isSelectedMode: boolean): DataObject[] {
    let widgets: DataObject[];

    if (isSelectedMode) {
      // Get selected widget ids from objectActionService and then filter the widgets from the store based on those ids

      const widgetIds = Array.from(
        this.objectActionService.selectedObjects.values(),
      )
        .filter((obj) => obj['widget_type_id'] !== undefined)
        .map((widget) => widget.id);

      widgets = this.widgets.filter((widget) => widgetIds.includes(widget.id));
    } else {
      // Get all widgets from current view
      widgets = this.widgets.filter(
        (widget) =>
          widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
      );
    }

    return widgets;
  }

  // * methods for handling Selected Widget operations - Start
  showCopyMoveDialog(isSelectedMode: boolean = false): void {
    let widgets = this.getWidgetListBySelectedMode(isSelectedMode);

    const dialogRef = this.dialogService.openSimpleDialog(
      MoveCopyWidgetDialogComponent,
      {
        data: {
          widgets: widgets,
          currentObject: this.currentObject,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed
      .pipe(take(1))
      .subscribe(
        (
          result: DialogCloseOptions<{ data: WidgetOperation[] } | undefined>,
        ) => {
          if (result.reason === DialogCloseReason.Save && result.data?.data) {
            this.runWidgetMassOperation(result.data.data);

            // * Clear selection if in selected mode
            if (isSelectedMode) {
              this.objectActionService.clearObjectSelection();
            }
          }
        },
      );
  }

  openPagePreview(): void {
    if (!this.currentObject) {
      return;
    }

    if (this.currentObject.object_class_id !== NomObjectClass.DASHBOARD) {
      return;
    }

    this.store
      .select(selectSelectedPageId)
      .pipe(take(1))
      .subscribe((pageId) => {
        if (pageId && this.currentObject) {
          this.presentationService.showSinglePagePresentation(
            this.currentObject.id,
            pageId,
          );
        }
      });
  }

  attachFiltersToSelected() {
    const selectedWidgetIds = Array.from(
      this.objectActionService.selectedObjects.values(),
    )
      .filter((obj) => obj['widget_type_id'] !== undefined)
      .map((widget) => widget.id);

    if (selectedWidgetIds.length === 0) return;

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          popupType: PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
      )
      .subscribe((event: any) => {
        this.widgetPersistence.applySettings(selectedWidgetIds, {
          dimension_id_filter: event.data,
        });
      });
  }

  attachPivotsToSelected() {
    const selectedWidgetIds = Array.from(
      this.objectActionService.selectedObjects.values(),
    )
      .filter((obj) => obj['widget_type_id'] !== undefined)
      .map((widget) => widget.id);

    if (selectedWidgetIds.length === 0) return;

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          popupType: PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
      )
      .subscribe((event: any) => {
        this.widgetPersistence.applySettings(selectedWidgetIds, {
          dimension_id_pivot: event.data,
        });
      });
  }

  detachFiltersFromSelected() {
    const selectedWidgetIds = Array.from(
      this.objectActionService.selectedObjects.values(),
    )
      .filter((obj) => obj['widget_type_id'] !== undefined)
      .map((widget) => widget.id);

    if (selectedWidgetIds.length === 0) return;

    const message = this.translateService.instant(
      'widget-actions.message.detach-filter',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.applySettings(selectedWidgetIds, {
            dimension_id_filter: [],
          });
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Detach filters cancelled: ${event.reason}`);
        }
      },
    );
  }

  detachPivotsFromSelected() {
    const selectedWidgetIds = Array.from(
      this.objectActionService.selectedObjects.values(),
    )
      .filter((obj) => obj['widget_type_id'] !== undefined)
      .map((widget) => widget.id);

    if (selectedWidgetIds.length === 0) return;

    const message = this.translateService.instant(
      'widget-actions.message.detach-pivot',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.applySettings(selectedWidgetIds, {
            dimension_id_pivot: [],
          });
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Detach pivots cancelled: ${event.reason}`);
        }
      },
    );
  }

  deleteSelectedWidgets() {
    const selectedWidgetIds = Array.from(
      this.objectActionService.selectedObjects.values(),
    )
      .filter((obj) => obj['widget_type_id'] !== undefined)
      .map((widget) => widget.id);

    if (selectedWidgetIds.length === 0) return;

    const message = this.translateService.instant(
      'widget-actions.message.delete-widget',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.removeWidgets(selectedWidgetIds);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }

  clearSelected() {
    this.store.dispatch(clearSelectedObjects());
  }

  private removeWidgets(widgetIds: string[]) {
    this.widgetPersistence.deleteWidgets(widgetIds);
  }

  private runWidgetMassOperation(operations: WidgetOperation[]) {
    this.widgetPersistence.massOperation(operations);
  }
  // * methods for handling Selected Widget operations - End
}
